<template>
  <div :class="clContainer()">
    <span v-if="label" :class="clSub('label', 'label')">{{ label }}</span>

    <div :class="clSub('errors')">
      <div
        v-for="(alert, index) in alerts"
        :class="clSub('error')"
        :key="index"
      >
        {{ alert.text }}
      </div>
    </div>

    <p v-if="items.length == 0" :class="textClass">
      {{ noValText }}
    </p>

    <container ref="initalItems" class="hidden">
      <slot :childChanged="childChanged"></slot>
    </container>
    <container ref="itemTemplates" v-if="!this.isReady" class="hidden">
      <slot
        name="itemTemplates"
        :state="state"
        :removeItem="removeItem"
        :childChanged="childChanged"
      ></slot>
    </container>

    <component
      :is="sortable ? 'draggable' : 'ul'"
      han
      v-model="items"
      v-bind="itemsContainerProps"
      v-on="itemsContainerEvents"
      ref="items"
      :class="clSub('items')"
    >
      <li
        v-for="(item, index) in items"
        :key="`li${item.id}`"
        :class="clSub('item')"
        :data-index="index"
      >
        <slot name="item-before">
          <div
            :class="clSub(['item-before', 'item-controls'])"
            v-if="state == 'edit'"
          >
            <button
              v-if="hasTemplates && showDefaultRemoveButton"
              type="button"
              @click="removeItem(index)"
              :key="index"
            >
              <img :src="getIconSrc('cross.svg')" />
            </button>

            <div
              :class="clSub('sortable-buttons')"
              v-if="isSortable && items.length > 1"
            >
              <button
                v-if="sortableArrows && index > 0"
                :class="clSub('sortable-buttons__up')"
                title="Eins nach oben verschieben"
                @click="moveItem(index, index - 1)"
              >
                <img :src="getIconSrc('arrow.svg')" />
              </button>
              <button
                v-if="sortableHandle"
                :class="clSub('sortable-handle')"
                title="Verschieben"
              >
                <img :src="getIconSrc('grip.svg')" />
              </button>
              <button
                v-if="sortableArrows && index < items.length - 1"
                :class="clSub('sortable-buttons__down')"
                title="Eins nach unten verschieben"
                @click="moveItem(index, index + 1)"
              >
                <img :src="getIconSrc('arrow.svg')" />
              </button>
            </div>
          </div>
          <div
            :class="clSub('sortable-buttons') + ' d-flex align-items-center'"
            v-if="sortableOnShow && items.length > 1"
          >
            <button
              v-if="sortableHandle"
              :class="clSub('sortable-handle') + ' sortable-handle-wauto'"
              title="Verschieben"
            >
              <img :src="getIconSrc('sortable-icon.svg')" />
            </button>
          </div>
        </slot>

        <clone-component
          :source="item.template"
          :key="item.id"
          :class="clSub('item-content')"
        ></clone-component>

        <slot name="item-after"></slot>
      </li>
    </component>

    <div v-if="hasTemplates && state == 'edit'" :class="clSub('buttons')">
      <slot name="buttons" :newItem="newItem" :data="childrenData"></slot>
    </div>
  </div>
</template>

<script>
import Base from "./Base";
import HasEditableChildren from "./Mixins/HasEditableChildren";
import NeedsIcons from "../../Mixins/NeedsIcons";
import draggable from "vuedraggable";
import { getCsrfToken } from "../../../helpers/forms";
import { popper } from "@popperjs/core/lib/enums";

export default {
  extends: Base,
  mixins: [HasEditableChildren, NeedsIcons],
  components: { draggable },
  props: {
    fromCheckList: {
      type: Boolean,
      default: false,
    },
    isReadyOnMounted: {
      type: Boolean,
      default: false,
    },
    typeClass: {
      type: String,
      default: "list",
    },
    // overwrite childrenAlwaysMultiple prop from HasEditableChildren mixin (set default to true)
    childrenAlwaysMultiple: {
      type: Boolean,
      default: true,
    },
    // overwrite updateDataOnChildRemove prop from HasEditableChildren mixin (set default to true)
    updateDataOnChildRemove: {
      type: Boolean,
      default: true,
    },
    showDefaultRemoveButton: {
      type: Boolean,
      default: true,
    },
    confirmRemove: {
      type: Boolean,
      default: false,
    },
    initialNewItems: {
      type: Array,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    sortableOnShow: {
      type: Boolean,
      default: false,
    },
    sortableHandle: {
      type: Boolean,
      default: false,
    },
    sortableArrows: {
      type: Boolean,
      default: false,
    },
    popups: {
      type: Object,
    },
  },
  data() {
    return {
      items: [],
      hasTemplates: false,
    };
  },
  computed: {
    containerClassModifiers() {
      const modifiers = this.styleMod.concat([this.state]);

      if (this.hasTemplates) {
        modifiers.push("has-templates");
      }
      if (this.isSortable) {
        modifiers.push("is-sortable");
      }

      return modifiers;
    },
    hasValue() {
      return this.items.length > 0;
    },
    isSortable() {
      return this.sortable && (this.sortableHandle || this.sortableArrows);
    },
    itemsContainerElement() {
      return this.sortable ? this.$refs.items.$el : this.$refs.items;
    },
    itemsContainerProps() {
      const props = {
        tag: "ul",
      };

      if (this.sortableHandle) {
        props.handle = `.${this.baseClass}__sortable-handle`;
      }

      return this.sortable ? props : {};
    },
    itemsContainerEvents() {
      return this.sortable
        ? {
            start: () => {
              this.stateBeforeSorting = this.state;
              this.state = "show";
            },
            end: (evt) => {
              this.state = this.stateBeforeSorting;
              this.itemMoved(evt.oldIndex, evt.newIndex);
            },
          }
        : {};
    },
  },
  created() {
    this.itemTemplates = [];
    this.initialNewItemsCount = 0;
    this.initialItemsReadyCount = 0;
  },
  mounted() {
    if (this.items.length == 0) {
      this.ready();
    }
  },
  methods: {
    onChildCreated(child) {
      let isItemTemplate =
        child.$parent.$vnode.data.ref &&
        child.$parent.$vnode.data.ref == "itemTemplates";
      let isInitialItem =
        child.$parent.$vnode.data.ref &&
        child.$parent.$vnode.data.ref == "initalItems";

      if (isItemTemplate || isInitialItem) {
        child.$options.render = function () {
          return null;
        };

        if (isItemTemplate) {
          let templateIndex = this.itemTemplates.length;
          this.itemTemplates.push(child);

          if (this.initialNewItems) {
            let initialNewItemIndex = this.initialNewItems.indexOf(
              templateIndex
            );

            if (initialNewItemIndex != -1) {
              this.newItem(child, null, false);
              this.initialNewItems.splice(initialNewItemIndex, 1);
              this.initialNewItemsCount++;
            }
          }

          if (!this.hasTemplates) {
            this.hasTemplates = true;
          }
        } else if (isInitialItem) {
          this.newItem(child, null, false);
        }
      } else {
        this.childCreated(child);
      }
    },
    onChildReady(child) {
      this.childReady(child);

      if (!this.isReady) {
        this.initialItemsReadyCount++;

        if (this.initialItemsReadyCount == this.items.length) {
          this.ready();
        }
      }
    },
    handleUnnamedChild() {
      return true;
    },
    newItem(template = 0, index = null, byUserInput = true) {
      let item = this.createNewItem(template);

      if (index === null) {
        this.items.push(item);
      } else {
        this.items.splice(index, 0, item);
      }

      if (byUserInput) {
        this.dataChanged(true);
      }
    },
    removeItem(index = null, byUserInput = true, ignoreConfirmation = false) {
      if (this.confirmRemove && this.popups && !ignoreConfirmation) {
        this.popups.confirm("Wirklich löschen?", (popup) => {
          popup.close();
          this.removeItem(index, byUserInput, true);
        });

        return false;
      }

      let removeChild = null;

      if (index instanceof Node) {
        let elem = index;

        while (elem.parentElement.parentElement != this.itemsContainerElement) {
          elem = elem.parentElement;
        }

        this.getEditableChildren().every((child, i) => {
          if (child.$el == elem) {
            removeChild = child;
            index = i;
            return false;
          }

          return true;
        });
      }

      this.items.splice(index, 1);

      if (this.fromCheckList) {
        this.allChildrenInstances.splice(index, 1);
        this.saveChecklistOrder(this.allChildrenInstances);
      } else {
        if (byUserInput) {
          this.dataChanged(true);
        }
      }
    },
    createNewItem(template) {
      if (typeof template == "number") {
        let templateChildren = this.getTemplateChildren();
        template = templateChildren[template];
      }

      return {
        template: template,
        id:
          "_listItem_" +
          this.name +
          "_" +
          Math.random().toString(36).substr(2, 9),
      };
    },
    getTemplateChildren() {
      let name = arguments.length > 0 ? arguments[0] : null;

      if (name !== null) {
        return this.itemTemplates.find((child) => child.baseName == name);
      } else {
        return this.itemTemplates;
      }
    },
    getEditableChildren(name = null) {
      return this.findEditableChildren(
        this.$children,
        (child) =>
          child.$el &&
          child.$el.parentElement.parentElement == this.itemsContainerElement,
        name,
        (child) => child.$children[0]
      );
    },
    set(name, data, triggerChange = false, byUserInput = false) {
      let targetChildren =
        name in this.childrenInstances ? this.childrenInstances[name] : [];

      if (
        (this.childrenAlwaysMultiple || targetChildren.length > 1) &&
        Array.isArray(data)
      ) {
        if (
          targetChildren.length > data.length &&
          (data.length > 0 || this.initialNewItemsCount == 0)
        ) {
          for (let i = data.length; i < targetChildren.length; i++) {
            this.items.splice(targetChildren[i].index);
          }
        }

        for (let i = 0; i < data.length; i++) {
          this.setChildData(name, data[i], i);
        }
      } else {
        this.setChildData(name, data, 0);
      }

      if (triggerChange) {
        this.dataChanged(byUserInput);
      }
    },
    addChild(name, index, data, byUserInput = true) {
      let templateChild = this.getTemplateChildren(name);

      if (templateChild) {
        this.newItem(templateChild, index, byUserInput);
      } else {
        throw new Error("Unknown template name " + name);
      }
    },
    itemMoved(from, to) {
      const moveChild = this.allChildrenInstances.find((c) => c.index == from);

      const span = from < to ? [from, to + 1] : [to - 1, from];
      const add = from < to ? -1 : 1;

      for (const child of this.allChildrenInstances) {
        if (child.index > span[0] && child.index < span[1]) {
          child.index += add;
        }
      }

      moveChild.index = to;

      this.resortChildrenInstances();
      this.updateDataFromChildren();
      this.saveChecklistOrder(this.allChildrenInstances);
    },

    saveChecklistOrder(items) {
      let clearItemsObj = {
        checklists: {
          checklist: [],
        },
      };
      items.map((item, index) => {
        let data = item.getData();

        let dataCheck = {
          deleted: data.deleted,
          name: data.name,
          id: data.id,
          for_assembling: data.for_assembling,
          for_servicing: data.for_servicing,
          description: data.description,
          part_area: data.part_area,
          part_type: data.part_type,
          sort_check: index,
          checks: [],
        };
        data.checks.map((check) => {
          dataCheck.checks.push(check);
        });
        clearItemsObj.checklists.checklist.push(dataCheck);
      });

      const formData = new FormData();

      this.buildFormData(formData, clearItemsObj);

      fetch("/settings/save-checklists/", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
        },
      }).then((resp) => {
        if (this.fromCheckList) {
          location.search = "?from=settings";
        }
        return resp.text();
      });
    },

    buildFormData(formData, data, parentKey) {
      if (
        data &&
        typeof data === "object" &&
        !(data instanceof Date) &&
        !(data instanceof File)
      ) {
        Object.keys(data).forEach((key) => {
          this.buildFormData(
            formData,
            data[key],
            parentKey ? `${parentKey}[${key}]` : key
          );
        });
      } else {
        const value = data == null ? "" : data;

        formData.append(parentKey, value);
      }
    },
    moveItem(from, to) {
      const moveItem = this.items[from];

      this.items.splice(from, 1);
      this.items.splice(to, 0, moveItem);

      this.itemMoved(from, to);
    },
  },
};
</script>